/**
 * Renders every Text object at the scale it is actually shown at.
 *
 * Phaser draws text into a texture sized from the font size in logical pixels,
 * then the 1280x720 stage is CSS-scaled up to fill the window - so on a
 * 1920px-wide desktop every label is stretched 1.5x and goes soft, the same
 * way the SVG art did before it was rasterised to match.
 *
 * `Text` already supports this per object through `style.resolution`. Rather
 * than add it to the ~60 call sites and rely on nobody forgetting it, the
 * factory behind `scene.add.text` is re-registered to supply it. A caller's
 * own style still wins, so an individual label can opt out.
 *
 * Deliberately not done by raising the canvas resolution: that would change
 * what one world unit means and every layout constant in the game with it.
 */
import Phaser from 'phaser';
import { artResolution } from './artScale';

type Factory = Phaser.GameObjects.GameObjectFactory;
type TextStyle = Phaser.Types.GameObjects.Text.TextStyle;

let installed = false;

export function installSharpText(): void {
  if (installed) return;
  installed = true;

  // Assigned to the prototype rather than passed to GameObjectFactory.register,
  // which only takes a name that is still free and silently ignores an attempt
  // to replace a built-in one.
  const factory = Phaser.GameObjects.GameObjectFactory.prototype as unknown as Record<
    string,
    unknown
  >;

  factory.text = function (
    this: Factory,
    x: number,
    y: number,
    text: string | string[],
    style?: TextStyle,
  ): Phaser.GameObjects.Text {
    // Half steps only: a fractional canvas size is truncated, which costs back
    // some of the sharpness this exists to buy.
    const resolution = Math.round(artResolution(this.scene) * 2) / 2;
    const object = new Phaser.GameObjects.Text(this.scene, x, y, text, {
      resolution,
      ...style,
    });
    return this.displayList.add(object) as Phaser.GameObjects.Text;
  };
}
